import EverestVisibilityDetector from '@components/common/EverestVisibilityDetector'
import { KeenSliderWrapper } from '@components/common/KeenSliderWrapper'
import { Button } from '@noissue-ui-kit/button'
import { ProductTile } from '@pageFeatures/category/ProductTile'
import { TProductTileData } from '@pageFeatures/category/ProductTile/ProductTile.types'
import {
  trackProductClick,
  trackProductImpressionForCategoryCollectionComponent,
} from '@utils/gtm'
import { useDevice } from '@utils/responsiveness/useDevice'
import { usePathname } from 'next/navigation'
import { useRef, useState, useEffect } from 'react'
import isEmpty from 'lodash/isEmpty'

export type TCategory = {
  categoryName: string
  productsInCategory: TProductTileData[]
}

type BreakpointConfig = {
  targetWidth: number
  slidesPerView: number
  spacing: number
  origin: 'auto' | null
}

const BREAKPOINT_CONFIGS: Record<string, BreakpointConfig> = {
  '(min-width: 640px)': {
    targetWidth: 640,
    slidesPerView: 2.2,
    spacing: 16,
    origin: 'auto',
  },
  '(min-width: 1024px)': {
    targetWidth: 1024,
    slidesPerView: 3.2,
    spacing: 12,
    origin: 'auto',
  },
  '(min-width: 1440px)': {
    targetWidth: 1440,
    slidesPerView: 3.6,
    spacing: 16,
    origin: 'auto',
  },
}

export function CategoryCollection({
  categoryCollection,
  trackingIdentifier,
}: {
  categoryCollection: TCategory[]
  trackingIdentifier: string
}) {
  const { isMobile } = useDevice()
  const pathname = usePathname()

  const [sliderBreakpoints, setSliderBreakpoints] = useState({})

  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0)
  const collectionRef = useRef<HTMLDivElement>(null)

  // If container width is smaller than breakpoint target width, we choose the largest fitting breakpoint
  // It helps in cases when container is smaller than the screen width. E.g on the narrow part of PDP layout
  const calculateSlidesPerView = (
    containerWidth: number,
    config: BreakpointConfig
  ): number => {
    if (!containerWidth || containerWidth > config.targetWidth) {
      return config.slidesPerView
    }

    const fittingBreakpoints = Object.entries(BREAKPOINT_CONFIGS)
      .filter(([, config]) => {
        return containerWidth >= config.targetWidth
      })
      .sort((a, b) => b[1].targetWidth - a[1].targetWidth)

    if (fittingBreakpoints?.length > 0) {
      return BREAKPOINT_CONFIGS[fittingBreakpoints[0][0]].slidesPerView
    }

    return config.slidesPerView
  }

  const getBreakpoints = (containerWidth: number) => {
    return Object.entries(BREAKPOINT_CONFIGS).reduce(
      (acc, [breakpoint, config]) => {
        acc[breakpoint] = {
          slides: {
            perView: calculateSlidesPerView(containerWidth, config),
            spacing: config.spacing,
            origin: config.origin,
          },
        }
        return acc
      },
      {} as Record<string, any>
    )
  }

  useEffect(() => {
    const updateWidth = () => {
      if (collectionRef.current) {
        const elementWidth = collectionRef.current.offsetWidth
        const newBreakpoints = getBreakpoints(elementWidth)
        setSliderBreakpoints(newBreakpoints)
      }
    }
    updateWidth()

    window.addEventListener('resize', updateWidth)

    return () => {
      window.removeEventListener('resize', updateWidth)
    }
  }, [])

  return (
    <div className="w-full @container">
      <div
        className="w-full px-4 @5xl:pl-[60px] @9xl:pl-[120px] mb-12 relative"
        ref={collectionRef}
      >
        {categoryCollection?.length > 1 && (
          <div className="flex w-[100vw] sm:w-full -translate-x-4 sm:translate-x-0 gap-[8px] mb-[32px] @5xl:mb-[64px] overflow-x-auto no-scrollbar">
            {categoryCollection?.map((category, index) => {
              return (
                <Button
                  onClick={() => {
                    setSelectedCategoryIndex(index)
                  }}
                  key={category?.categoryName}
                  className={`first:ml-8 sm:first:ml-0 cursor-pointer text-[18px] whitespace-nowrap ${
                    index === selectedCategoryIndex &&
                    'text-pitaya font-semibold'
                  }`}
                  variant="primary"
                  colour={`${
                    index === selectedCategoryIndex ? 'pitaya' : 'pale'
                  }`}
                  corners="rounded"
                  size={isMobile ? 's' : 'm'}
                >
                  {category?.categoryName}
                </Button>
              )
            })}
          </div>
        )}

        {!isEmpty(sliderBreakpoints) && (
          <KeenSliderWrapper
            initialConfig={{
              perView: 1.17,
              spacing: 16,
              origin: 'auto',
            }}
            breakpoints={sliderBreakpoints}
            isShowArrows={!isMobile}
            hideArrowsWhenDisabled={true}
            className="!hidden sm:!flex !overflow-visible overflow-y-hidden grow-0 h-[440px] @3xl:h-[500px] @8xl:h-[600px] @11xl:h-[500px] @12xl:h-[600px]"
            trackingIdentifier={trackingIdentifier}
            key={selectedCategoryIndex}
          >
            {categoryCollection[selectedCategoryIndex]?.productsInCategory?.map(
              (product, index) => {
                return (
                  <div
                    className="keen-slider__slide !overflow-visible"
                    key={product.productName + '_' + index}
                  >
                    <EverestVisibilityDetector
                      key={product.productName}
                      once
                      onVisible={() => {
                        trackProductImpressionForCategoryCollectionComponent({
                          productName: product?.productName,
                          slug: product?.urlSlug,
                          pageUrl: pathname,
                          componentIdentifier: trackingIdentifier,
                        })
                      }}
                      className="hover:z-5"
                    >
                      <ProductTile
                        linkUrl={product.linkUrl}
                        productName={product.productName}
                        imageTags={product.imageTags}
                        productTags={product.productTags}
                        pricePerUnit={product.pricePerUnit}
                        salePricePerUnit={product.salePricePerUnit}
                        primaryImage={product.primaryImage}
                        imageBackgroundColor={product.imageBackgroundColor}
                        productDetails={product.productDetails}
                        productType={product.productType}
                        onClick={() => {
                          trackProductClick({
                            list: `${trackingIdentifier}`,
                            buttonText: '',
                            item: {
                              name: product?.productName,
                              slug: product?.urlSlug,
                              pricePerUnit:
                                product?.pricePerUnit?.toString() || '',
                            },
                            position: index,
                          })
                        }}
                      />
                    </EverestVisibilityDetector>
                  </div>
                )
              }
            )}
          </KeenSliderWrapper>
        )}

        <div className="flex flex-wrap sm:hidden">
          {categoryCollection[selectedCategoryIndex]?.productsInCategory?.map(
            (product, index) => {
              return (
                <EverestVisibilityDetector
                  key={product.productName}
                  once
                  onVisible={() => {
                    trackProductImpressionForCategoryCollectionComponent({
                      productName: product?.productName,
                      slug: product?.urlSlug,
                      pageUrl: pathname,
                      componentIdentifier: trackingIdentifier,
                    })
                  }}
                  className="!w-1/2"
                >
                  <ProductTile
                    linkUrl={product.linkUrl}
                    productName={product.productName}
                    imageTags={product.imageTags}
                    productTags={product.productTags}
                    pricePerUnit={product.pricePerUnit}
                    salePricePerUnit={product.salePricePerUnit}
                    primaryImage={product.primaryImage}
                    imageBackgroundColor={product.imageBackgroundColor}
                    productDetails={product.productDetails}
                    productType={product.productType}
                    compactLayout={true}
                    onClick={() => {
                      trackProductClick({
                        list: `${trackingIdentifier}`,
                        buttonText: '',
                        item: {
                          name: product?.productName,
                          slug: product?.urlSlug,
                          pricePerUnit: product?.pricePerUnit?.toString() || '',
                        },
                        position: index,
                      })
                    }}
                  />
                </EverestVisibilityDetector>
              )
            }
          )}
        </div>
      </div>
    </div>
  )
}
